/**
 * Header component
 */

// React
import React from 'react';

// Libraries
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useNavigate } from 'react-router-dom';

// MUI Components
import { styled } from '@mui/material/styles';
import {
  Button,
  Box,
  ListItemText,
  Menu,
  IconButton,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';

// Recoil
import { userInfoAtom } from 'recoil/UserInfoRecoil';
import { loadingAtom } from 'recoil/LoadingRecoil';

// Common
import { SSO_URL, STORAGE_KEYS, AWS_S3 } from 'utils/constants';

// Router
import ROUTES from 'configs/route';

// Service
import AuthService from 'services/AuthService';

// Styles
const HeaderContainer = styled('nav')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '15px 30px',
  boxShadow: '0px 0px 8px 0px #cccc',
  position: 'relative',
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.down('sm')]: {
    padding: '10px',
  },
}));

const Logo = styled('img')({
  width: 'auto',
  height: '22px',
  objectFit: 'cover',
  cursor: 'pointer',
});

const MenuItems = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
});

const LoginButton = styled(Button)(({ theme }) => ({
  fontWeight: 600,
  height: '30px',
  width: '100px',
  padding: '0px 20px',
  borderRadius: '30px',
  fontSize: '14px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px',
  },
}));

const WrapperMenu = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const UserID = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '14px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
  },
}));

const BlockNameAndEmail = styled(Box)(({ theme }) => ({
  padding: '26px',
  width: '100%',

  '& p': {
    fontSize: '14px',
    fontWeight: 'bold',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '18px',
  },
}));

const BlockName = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'pre',
});

const BlockEmail = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const BlockLogout = styled(Box)(({ theme }) => ({
  padding: '26px',
  width: '100%',

  '& span': {
    fontSize: '12px',
    fontWeight: '600',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '18px',
  },
}));

// Component
const Header: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const setIsLoading = useSetRecoilState(loadingAtom);

  const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || '';
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
  const { last_name = '', first_name = '', email = '' } = useRecoilValue(userInfoAtom);
  const fullName = last_name + '　' + first_name;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const urlOrigin = window.location.origin;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLoginClick = () => {
    window.location.href = `${SSO_URL.LOGIN}?client_id=${SSO_URL.CLIENT_ID}&redirect_uri=${urlOrigin}`;
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = async () => {
    handleClose();
    try {
      setIsLoading(true);
      await AuthService.logout();
      setIsLoading(false);
      navigate(ROUTES.HOME);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  const handleNavigateUserInfo = () => {
    if (accessToken && refreshToken) {
      navigate(ROUTES.USER_INFO);
    } else {
      navigate(ROUTES.HOME);
    }
  };

  return (
    <HeaderContainer>
      <Logo onClick={handleNavigateUserInfo} src={`${AWS_S3.LOGO_PNG}`} alt="App Logo" />
      <MenuItems></MenuItems>
      {!accessToken || !refreshToken ? (
        <LoginButton variant="contained" color="primary" onClick={handleLoginClick}>
          ログイン
        </LoginButton>
      ) : (
        <>
          <WrapperMenu>
            <UserID variant="h4">ユーザ ID</UserID>
            <IconButton onClick={handleClick}>
              <MenuIcon />
            </IconButton>
          </WrapperMenu>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            PaperProps={{
              sx: {
                width: 235,
                padding: 0,
                marginTop: 2.5,
                borderRadius: '10px',
                [theme.breakpoints.down('sm')]: {
                  marginTop: 1.5,
                },
              },
            }}
          >
            <MenuItem onClick={handleClose}>
              <Box sx={{ background: alpha(theme.palette.primary.main, 0.2), width: '100%' }}>
                <BlockNameAndEmail>
                  <BlockName>{fullName}</BlockName>
                  <BlockEmail>{email}</BlockEmail>
                </BlockNameAndEmail>
              </Box>
            </MenuItem>

            <MenuItem onClick={handleLogoutClick}>
              <BlockLogout>
                <ListItemText>ログアウト</ListItemText>
              </BlockLogout>
            </MenuItem>
          </Menu>
        </>
      )}
    </HeaderContainer>
  );
};

export default Header;
